import { defineStore } from "pinia";
import { config } from "../config.js";
import {
  getOrCreateUserProfile,
  setUserEventsFirebase,
  saveUserProfile,
  authenticateWithCustomToken,
} from "@/firestoreModel";
import { watch } from "vue";
import { useEventsStore } from "./events.js";

function fetchUserData() {
  function parseJsonACB(response) {
    return response.json();
  }

  return fetch(`${config.backendUrl}/whoami`, { credentials: "include" })
    .catch(() => {
      {
      }
    })
    .then(parseJsonACB);
}

function retrieveJWT() {
  function parseACB(data) {
    return data
      .json()
      .then((d) => d?.token || { error: "Token somehow not included" });
  }

  return fetch(`${config.backendUrl}/firebase-token`, {
    credentials: "include",
  })
    .catch(() => {
      error: "Can't get JWT token";
    })
    .then(parseACB);
}

// TODO: Make sure that logout clears the store data

export const useUserDataStore = defineStore("userData", {
  state: () => ({
    isLoaded: false,
    isLoggedIn: false,
    steamId: null,
    steamName: null,
    avatarUrl: null,
    events: [],
    userProfile: null,
  }),

  actions: {
    async init() {
      // Retrieve the userdata through the model
      const session = (await fetchUserData())?.session?.user;

      // If we are logged in and receive user details, this is the information that should at least be expected.
      if (session?.steamid) {
        this.steamName = session.username;
        this.steamId = session.steamid;
        this.avatarUrl = session.avatar.large;
        this.isLoggedIn = true;

        // Login to firestore
        // As we are logged in, we should make sure to grab our JWT token and authenticate towards firebase
        // This is only doable after steam authentication and once at startup, hence why it's done from here.
        const token = await retrieveJWT();


        // Most likely valid
        if (!token?.error) {
          await authenticateWithCustomToken(token);
        }

        // load user details from firebase model
        const userProfile = await getOrCreateUserProfile(session);

        if (userProfile?.events) {
          this.events = userProfile.events || [];
        }

        this.userProfile = userProfile;

        // setup persistence of events to firestore
        watch(
          () => this.events,
          (newEvents) => {
            setUserEventsFirebase(this.steamId, this.events);
          },
          { deep: true },
        );

        let eventsStore = useEventsStore();

        // Filter out invalid events left from deleted events or similar
        watch(
          () => eventsStore.eventsReady,
          (loaded) => {
            if (loaded) {

              this.events = this.events
                .slice(0)
                .filter((e) => eventsStore.events.map((e) => e.id).includes(e));
            }
          },
          { once: true },
        );

        // Watch for changes in user profile (nickname, avatarUrl) and persist to Firestore
        watch(
          () => [this.userProfile.nickname, this.userProfile.avatarUrl],
          (newValues) => {
            if (this.isLoggedIn && this.steamId) {
              saveUserProfile(
                { uid: this.steamId },
                {
                  nickname: this.userProfile.nickname,
                  avatarUrl: this.userProfile.avatarUrl,
                },
              );
            }
          },
        );
      }

      this.isLoaded = true;
    },
    addEvent(eventId) {
      if (!this.events.includes(eventId)) {
        this.events.push(eventId);
      }
      // Persistence handled by the watcher in init()
    },
    removeEvent(eventId) {
      if (this.events.includes(eventId)) {
        this.events.splice(this.events.indexOf(eventId), 1);
      }
    },
    setName(name) {
      this.userProfile.nickname = name;
    },
  },
});
